import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ShoppingCart,
  X,
  TrendingUp,
} from 'lucide-react';
import { useSearch, useCategories, useSettings } from '@/hooks/useProducts';
import BrandSymbol from '@/components/BrandSymbol';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { cn, formatCurrency } from '@/lib/utils';
import Button from '@/components/ui/Button';

function ProductCard({ product }: { product: any }) {
  const { t } = useTranslation('search');
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);
  const [isAdding, setIsAdding] = useState(false);

  // GET /api/search answers with base_price / primary_image_url, and names a
  // variant only when the product has exactly one (default_variant_id); a
  // product with options has to be chosen on its own page.
  const defaultVariant = product.variants?.[0];
  const variantId: string | undefined = defaultVariant?.id ?? product.default_variant_id ?? undefined;
  const price = defaultVariant?.price ?? product.price ?? product.base_price ?? 0;
  const imageUrl = product.images?.[0]?.url ?? product.image_url ?? product.primary_image_url;

  async function handleAddToCart(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!variantId) return;
    setIsAdding(true);
    try {
      await addItem(variantId);
      addToast(t('card.addedToast'), 'success');
    } catch {
      addToast(t('card.addFailedToast'), 'error');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <article className="group rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden transition-shadow hover:shadow-md">
      <Link
        to={`/product/${product.slug}`}
        className="block aspect-square overflow-hidden bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-inset"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <BrandSymbol className="h-12 w-12 text-stone-300" />
          </div>
        )}
      </Link>
      <div className="p-4">
        <Link
          to={`/product/${product.slug}`}
          className="block text-sm font-semibold text-stone-900 hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
        >
          <h3>{product.name}</h3>
        </Link>
        {product.category_name && (
          <p className="mt-1 text-xs text-stone-500">{product.category_name}</p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-base font-bold text-sage-700">
            {formatCurrency(price)}
          </span>
          <Button
            size="sm"
            onClick={handleAddToCart}
            isLoading={isAdding}
            disabled={!variantId}
            aria-label={t('card.addToCartAriaLabel', { name: product.name })}
          >
            <ShoppingCart className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only sm:not-sr-only">{t('card.addToCart')}</span>
          </Button>
        </div>
      </div>
    </article>
  );
}

export default function SearchPage() {
  const { t } = useTranslation('search');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [inputValue, setInputValue] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { data, isLoading } = useSearch(debouncedQuery);
  const { data: categoriesData } = useCategories();
  const { data: settingsData } = useSettings();

  const defaultSuggestions = useMemo(
    () => [
      t('defaultSuggestions.newArrivals'),
      t('defaultSuggestions.bestSellers'),
      t('defaultSuggestions.onSale'),
      t('defaultSuggestions.giftIdeas'),
    ],
    [t],
  );

  const suggestions: string[] = settingsData?.settings?.search_suggestions ?? defaultSuggestions;
  const placeholder = settingsData?.settings?.search_placeholder ?? t('placeholderDefault');

  const products = data?.products ?? [];
  const total = data?.total ?? data?.pagination?.total ?? 0;
  const categories = categoriesData?.categories ?? [];

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        setDebouncedQuery(value);
        if (value) {
          setSearchParams({ q: value });
        } else {
          setSearchParams({});
        }
      }, 300);
    },
    [setSearchParams]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function handleSuggestionClick(suggestion: string) {
    setInputValue(suggestion);
    setDebouncedQuery(suggestion);
    setSearchParams({ q: suggestion });
  }

  function handleClear() {
    setInputValue('');
    setDebouncedQuery('');
    setSearchParams({});
    inputRef.current?.focus();
  }

  const showSuggestions = !debouncedQuery || debouncedQuery.length < 2;

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-stone-900">{t('heading')}</h1>

      {/* Search input */}
      <div className="mt-6">
        <label htmlFor="search-input" className="sr-only">
          {t('label')}
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            id="search-input"
            type="search"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            className={cn(
              'w-full rounded-xl border border-stone-300 bg-white py-3 pl-12 pr-10 text-base text-stone-900',
              'placeholder:text-stone-400',
              'focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-transparent'
            )}
            aria-describedby="search-status"
            autoComplete="off"
          />
          {inputValue && (
            <button
              onClick={handleClear}
              className={cn(
                'absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-stone-400 hover:text-stone-600',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500'
              )}
              aria-label={t('clearAriaLabel')}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Live region for screen readers */}
      <div id="search-status" className="sr-only" aria-live="polite" aria-atomic="true">
        {isLoading
          ? t('live.searching')
          : debouncedQuery.length >= 2
            ? t('live.results', { count: total, query: debouncedQuery })
            : ''}
      </div>

      {/* Suggestions (when search is empty) */}
      {showSuggestions && (
        <div className="mt-10">
          <div className="mb-8">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-stone-900">
              <TrendingUp className="h-5 w-5 text-sage-600" aria-hidden="true" />
              {t('popularSearches')}
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={cn(
                    'rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700',
                    'hover:border-sage-300 hover:bg-sage-50 hover:text-sage-800 transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500'
                  )}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          {categories.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-stone-900">
                {t('browseCategories')}
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {categories.map((cat: any) => (
                  <Link
                    key={cat.id}
                    to={`/shop/${cat.slug}`}
                    className={cn(
                      'rounded-lg border border-stone-200 bg-white p-4 text-center transition-all',
                      'hover:border-sage-300 hover:shadow-sm',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500'
                    )}
                  >
                    <span className="text-sm font-medium text-stone-900">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {!showSuggestions && isLoading && (
        <div
          className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          aria-busy="true"
          aria-label={t('loadingResultsAriaLabel')}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-xl border border-stone-200 bg-white"
              aria-hidden="true"
            >
              <div className="aspect-square bg-stone-200" />
              <div className="p-4 space-y-3">
                <div className="h-4 rounded bg-stone-200 w-3/4" />
                <div className="h-4 rounded bg-stone-200 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {!showSuggestions && !isLoading && products.length > 0 && (
        <div className="mt-8">
          <p className="mb-6 text-sm text-stone-500">
            {t('resultsCount', { count: total, query: debouncedQuery })}
          </p>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((product: any) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}

      {/* No results */}
      {!showSuggestions && !isLoading && products.length === 0 && (
        <div className="mt-16 text-center">
          <Search className="mx-auto h-12 w-12 text-stone-300" aria-hidden="true" />
          <h2 className="mt-4 text-lg font-semibold text-stone-900">
            {t('noResults.heading')}
          </h2>
          <p className="mt-2 text-stone-600">
            {t('noResults.description', { query: debouncedQuery })}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {suggestions.slice(0, 4).map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                className={cn(
                  'rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700',
                  'hover:border-sage-300 hover:bg-sage-50 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500'
                )}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
